<script>
  // Import Firebase
  import firebase from "firebase/compat/app";
  import "firebase/compat/auth";
  import "firebase/compat/firestore";
  import "firebase/compat/database";

  // Import Svelte
  import { onMount } from "svelte";

  // Import Components
  import { log, announce } from "./components/Logging.svelte";
  import Config, { validatePrefs } from "./components/Config.svelte";
  import { newUserDocument } from "./prefsDefaults.js";
  import { ensureDirectoryEntry } from "./directory.js";
  import { ensureProfile } from "./profiles.js";
  import { prefetchTurnCredentials } from "./turn.js";
  import { activateAppCheck } from "./appCheck.js";
  import { get } from "svelte/store";
  import { isNative, nativeInit, nativeNotifyAuth, nativeNotifyPrefs, nativeOpen, shellMode } from "./native.js";
  import { startAutoUpdate } from "./autoUpdate.js";
  import UserState from "./components/UserState.svelte";
  import Connections from "./components/Connections.svelte";
  import FaceDetection from "./components/FaceDetection.svelte";
  import Navigation from "./components/Navigation.svelte";
  import Loading from "./components/Loading.svelte";
  import Welcome from "./components/Welcome.svelte";
  import FriendList from "./components/FriendList.svelte";
  import InactiveWindow from "./components/InactiveWindow.svelte";
  import VerifyBanner from "./components/VerifyBanner.svelte";
  import AuthAction from "./components/AuthAction.svelte";
  import { actionFromUrl } from "./verification.js";
  import { inviteFromUrl, pendingInvite, pendingInviter, fetchInvite, acceptInvite } from "./invites.js";
  import { handoffTokenFromUrl } from "./handoff.js";
  import PeerWindow from "./components/PeerWindow.svelte";
  import LinkWindow from "./components/LinkWindow.svelte";
  import { linkOpenWith } from "./callState.js";

  // Init Firestore
  var firebaseConfig = {
    apiKey: "AIzaSyDfPklXWWQ0j9J1Z3tzIeWI6H6iJCNB4W0",
    authDomain: "knolval-3f384.firebaseapp.com",
    databaseURL: "https://knolval-3f384.firebaseio.com",
    projectId: "knolval-3f384",
    storageBucket: "knolval-3f384.appspot.com",
    messagingSenderId: "107496587808",
    appId: "1:107496587808:web:8661747c7f966183c3b245",
  };
  firebase.initializeApp(firebaseConfig);
  activateAppCheck(firebase);
  const auth = firebase.auth();
  const db = firebase.firestore();
  // ?invite=<code> from a friend's link: parked until the person is signed in, and
  // the inviter's name shown on the sign-in page meanwhile.
  const inviteCode = inviteFromUrl();
  if (inviteCode) {
    fetchInvite(db, inviteCode)
      .then((i) => pendingInviter.set(i ? { code: inviteCode, name: i.name } : null))
      .catch((e) => console.warn("Could not read the invite", e));
  }
  // Name of the friend whose link this account just accepted (welcome page notice).
  let invitedBy = null;
  function settleInvite() {
    const code = pendingInvite();
    if (!code || !user) return;
    acceptInvite(db, firebase, user, code)
      .then((inviter) => {
        pendingInviter.set(null);
        if (inviter) {
          invitedBy = inviter.name || "Your friend";
          console.log("Invite accepted; now friends with", inviter.uid.slice(0, 6));
        }
      })
      .catch((e) => console.warn("Could not accept the invite", e));
  }

  // Declart defaults
  export let version;
  let a = "c:App";
  let user = null;
  let mode = "loading"; // modes: loading config welcomeWindow friendList window native
  // An emailed link (confirm address, reset password) lands here with mode and oobCode
  // in the URL; AuthAction handles it over everything else until the person continues.
  let authAction = actionFromUrl();
  function finishAuthAction() {
    authAction = null;
    refreshVerification();
  }
  // Inside the iOS app the page shows only the window; every other screen is native.
  let prefsValid = false;
  // A device that was on the window page comes back to it by itself after a reload
  // (the auto-update, a crash, F5), instead of waiting on the welcome page for a click.
  const RESUME_KEY = "passcall.resumeWindow";
  function resumeWindow() {
    try { return window.sessionStorage.getItem(RESUME_KEY) === "1"; } catch (e) { return false; }
  }
  $: try {
    if (mode === "window") window.sessionStorage.setItem(RESUME_KEY, "1");
    else if (mode !== "loading") window.sessionStorage.removeItem(RESUME_KEY);
  } catch (e) { /* storage unavailable */ }
  function nativeTarget() {
    return get(shellMode) === "window" && prefsValid ? "window" : "native";
  }
  // Fetch TURN credentials as soon as the window opens, not when the first match lands.
  $: if (mode === "window" && user) prefetchTurnCredentials(user);
  let prefs = {
    name: "",
    email: "",
    videoSource: null,
    audioSource: null,
    debug: false,
    fullscreen: false,
    minimumFaceScore: 80,
    faceDetectionSlack: 15004,
    faceDetectionInterval: 1004,
    detectionResolution: "640x480",
    transmitFramerate: 24,
    transmitResolution: "1280x720",
    selfView: false,
    dnd: false,
    forceRelay: false,
    videoCodec: "auto",
    detectorPath: "stream",
    chime: true,
    keepReady: true,
    // peerId: ""
  };
  let status = [{ name: "Version", value: version }];
  let userStateObj;
  // The window stays closed (and the device never reports "available") until the
  // account's email address is confirmed.
  let emailVerified = false;
  // While the address is unconfirmed, check every few seconds (every minute after
  // a quarter of an hour) so the notice clears by itself once the link is clicked,
  // usually on another device.
  let verifyPoll = null;
  $: if (user && !emailVerified && !isNative) {
    if (!verifyPoll) {
      const started = Date.now();
      const tick = async () => {
        await refreshVerification();
        if (!verifyPoll) return;
        verifyPoll = setTimeout(tick, Date.now() - started > 15 * 60 * 1000 ? 60000 : 5000);
      };
      verifyPoll = setTimeout(tick, 5000);
    }
  } else if (verifyPoll) {
    clearTimeout(verifyPoll);
    verifyPoll = null;
  }
  // Report offline first (while still signed in), then sign out.
  async function goOffline() {
    if (!userStateObj) return;
    try {
      await Promise.race([userStateObj.goOffline(), new Promise((r) => setTimeout(r, 2000))]);
    } catch (e) {
      console.warn("Offline write before sign-out failed", e);
    }
  }
  async function signOutUser() {
    await goOffline();
    auth.signOut().catch((e) => console.warn("Sign out failed", e));
  }
  async function refreshVerification() {
    const current = firebase.auth().currentUser;
    if (!current) return;
    try {
      await current.reload();
    } catch (e) {
      console.warn("Could not refresh the account", e);
    }
    user = firebase.auth().currentUser;
    emailVerified = !!(user && user.emailVerified);
  }

  onMount(() => {
    announce("App.svelte Mounted");
    startAutoUpdate();
    if (isNative) {
      nativeInit({
        firebase,
        auth,
        db,
        version,
        getPrefs: () => prefs,
        applyPrefs: (next) => {
          for (const property in prefs) {
            if (property in next) prefs[property] = next[property];
          }
        },
        validatePrefs,
        goOffline,
        onVerificationChanged: (verified) => {
          emailVerified = verified;
        },
      });
      shellMode.subscribe(function () {
        if (user && mode !== "loading") mode = nativeTarget();
      });
    }
    // Registered on passcall.io a moment ago: the site hands over a custom token so
    // the person lands signed in, instead of typing the password a second time.
    const handoff = handoffTokenFromUrl();
    if (handoff) {
      auth.signInWithCustomToken(handoff).catch((e) => console.warn("Sign-in hand-off from the site failed; showing the sign-in form", e && e.code));
    }
    setTimeout(function () {
      auth.onAuthStateChanged(function (result) {
        user = result;
        if (!user) {
          user = null;
        }
        emailVerified = !!(user && user.emailVerified);
        if (isNative) nativeNotifyAuth(user);
        updateMode();
      });
    }, 500);
    return () => {};
  });

  /*
  Update debug stats
  */
  function updateDebugStats(event) {
    let objIndex = status.findIndex((obj) => obj.name == event.detail.property);
    let clear = false;
    if (event.detail.clear) {
      clear = true;
    }
    // console.log(status,objIndex);
    if (clear) {
      if (objIndex !== -1) {
        status.splice(objIndex, 1);
      }
    } else {
      if (objIndex !== -1) {
        // console.log('update val');
        status[objIndex]["value"] = event.detail.value;
      } else {
        // console.log('add val');
        status.push({ name: event.detail.property, value: event.detail.value });
      }
    }
  }

  function updateMode(change) {
    if (user) {
      // User is signed in.
      console.log("updateMode: Detected user signed in");
      // Get user preferences/defaults before loading window
      updatePrefs(user.uid).then(function (result) {
        console.log(result);
        prefsValid = !!result;
        settleInvite();
        if (isNative) {
          nativeNotifyPrefs(prefs, prefsValid);
          mode = nativeTarget();
          return;
        }
        if (result) {
          if (change) {
            mode = change.detail.action;
          } else if (resumeWindow()) {
            console.log("updateMode: resuming the window after a reload");
            mode = "window";
          } else {
            mode = "welcomeWindow";
          }
          // check if the preferences were validated
        } else {
          mode = "config";
        }
      });
    } else {
      // No user is signed in.
      console.log("updateMode: Detected user signed out");
      prefsValid = false;
      mode = isNative ? "native" : "config";
    }
  }

  function openConfig() {
    console.log("open app");
    if (isNative) { nativeOpen("settings"); return; }
    mode = "config";
  }

  function openFriendList() {
    console.log("open app");
    if (isNative) { nativeOpen("friends"); return; }
    mode = "friendList";
  }

  /* 
  Update preferences and return whether or not they are valid
  */
  // A freshly registered account writes its Users document right after auth
  // fires, so a missing document is retried a few times before giving up.
  const PREFS_RETRY_LIMIT = 4;
  let directoryChecked = false;
  const PREFS_RETRY_DELAY_MS = 1000;

  function updatePrefs(uid, attempt = 0) {
    var docRef = db.collection("Users").doc(uid);
    return docRef
      .get()
      .then(function (doc) {
        if (doc.exists) {
          let data = doc.data();
          if (!directoryChecked) {
            directoryChecked = true;
            ensureDirectoryEntry(db, firebase.auth().currentUser, data.name).catch(function (error) {
              console.warn("Could not update directory entry", error);
            });
            ensureProfile(db, firebase.auth().currentUser, data.name).catch(function (error) {
              console.warn("Could not update profile", error);
            });
          }
          let validator = validatePrefs(data);
          console.log("validator", validator.isValid);
          for (const property in prefs) {
            // Keys added after an account was created keep their default.
            if (property in data) prefs[property] = data[property];
          }
          return validator.isValid;
        } else if (attempt < PREFS_RETRY_LIMIT) {
          console.log("User document not found, retrying", attempt + 1);
          return new Promise(function (resolve) {
            setTimeout(resolve, PREFS_RETRY_DELAY_MS);
          }).then(function () {
            return updatePrefs(uid, attempt + 1);
          });
        } else if (attempt > PREFS_RETRY_LIMIT) {
          console.log("User document still missing after creating it");
        } else {
          // The account exists but its Users document is gone (reset, cleanup, or a
          // sign-up that failed half-way). Recreate it from the account and read it back.
          console.log("User document not found; creating it from the account");
          var current = firebase.auth().currentUser;
          return docRef
            .set(
              newUserDocument({
                name: (current && current.displayName) || "",
                email: (current && current.email) || "",
                createdAt: firebase.firestore.FieldValue.serverTimestamp(),
              })
            )
            .then(function () {
              return updatePrefs(uid, PREFS_RETRY_LIMIT + 1);
            });
        }
      })
      .catch(function (error) {
        console.log("Error getting User document:", error);
      });
  }
</script>

<div>
  <section class="hero has-background-black is-fullheight">
    {#if authAction}
      <AuthAction {auth} action={authAction} on:done={finishAuthAction} />
    {/if}
    {#if user && !emailVerified && !isNative && !authAction && (mode === "window" || mode === "welcomeWindow")}
      <VerifyBanner {user} on:refresh={refreshVerification} />
    {/if}
    {#if mode == "loading"}
      <Loading />
    {:else if mode == "native"}
      <!-- The iOS app draws its own screens over this. -->
      <div class="native-idle"></div>
    {:else if mode == "welcomeWindow"}
      <Welcome {prefs} {firebase} {auth} {invitedBy} on:modeChange={updateMode} on:openConfig={openConfig} on:openFriendList={openFriendList} />
    {:else if mode == "friendList"}
      <FriendList {prefs} {firebase} {auth} on:modeChange={updateMode} on:openConfig={openConfig} on:openFriendList={openFriendList} />
    {:else if mode == "window"}
      <Navigation {prefs} {status} on:openConfig={openConfig} on:signOut={signOutUser} />
      <UserState
        bind:this={userStateObj}
        {firebase}
        {auth}
        {db}
        {user}
        ready={!!prefs.keepReady}
        on:debugStatus={updateDebugStats}
      >
        <FaceDetection
          {user}
          {prefs}
          blocked={prefs.dnd || !emailVerified}
          let:stream
          let:present
          on:debugStatus={updateDebugStats}
        >
          {#if prefs.keepReady && emailVerified}
            <LinkWindow {user} {db} {prefs} {present} {stream} on:debugStatus={updateDebugStats} />
          {/if}
          <Connections {user} {db} let:connection>
            {#if user && connection.roomName && !$linkOpenWith}
              {#if !prefs.dnd && emailVerified}
                <PeerWindow
                  {user}
                  {db}
                  {stream}
                  {connection}
                  {prefs}
                  on:debugStatus={updateDebugStats}
                />
              {/if}
            {:else}
              <InactiveWindow {prefs} />
            {/if}
          </Connections>
        </FaceDetection>
      </UserState>
    {:else}
      <!-- config -->
      <Config {firebase} {auth} on:modeChange={updateMode} />
    {/if}
  </section>
</div>

<style>
  .native-idle { min-height: 100vh; background: #0f2a1e; }
</style>
